import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Users, Car, TrendingUp, Plus, AlertCircle, UserCheck, UserX, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const SystemAdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalLocations: 0,
    totalPatrons: 0,
    totalStaff: 0,
    totalRevenue: 0,
    activeLocations: 0,
    inactivePatrons: 0
  });
  const [recentPatrons, setRecentPatrons] = useState([]);
  const [systemOverview, setSystemOverview] = useState({
    locationsToday: 0,
    transactionsToday: 0,
    revenueToday: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSystemAdminData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      // Sistem geneli istatistikleri yükle
      const [statsResponse, usersResponse] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers()
      ]);

      if (statsResponse.data.success) {
        const basicStats = statsResponse.data.data;
        setStats(prev => ({
          ...prev,
          totalLocations: basicStats.totalLocations,
          activeLocations: basicStats.activeLocations,
          totalRevenue: basicStats.totalRevenue
        }));
      }

      if (usersResponse.data.success) {
        const users = usersResponse.data.data;
        const patrons = users.filter(u => u.user_type === 'patron');
        const staff = users.filter(u => u.user_type === 'staff');

        setStats(prev => ({
          ...prev,
          totalPatrons: patrons.length,
          totalStaff: staff.length,
          inactivePatrons: patrons.filter(p => !p.is_active).length
        }));

        // En son kayıt olan patronları göster
        setRecentPatrons(
          patrons
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
        );
      }

    } catch (err) {
      console.error('Failed to load system admin data:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSystemAdminData();
  }, [loadSystemAdminData]);

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
        <UserCheck className="w-3 h-3 mr-1" />
        Aktif
      </span>
    ) : (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center">
        <UserX className="w-3 h-3 mr-1" />
        Pasif
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-3">
        <Link to="/admin/locations/new" className="btn-primary flex items-center w-full sm:w-auto justify-center">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Lokasyon
        </Link>
        <Link to="/admin/users/new" className="btn-secondary flex items-center w-full sm:w-auto justify-center">
          <Users className="w-4 h-4 mr-2" />
          Yeni Kullanıcı
        </Link>
      </div>

      {/* System Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="h-7 w-7 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Lokasyon</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalLocations}</p>
                <p className="text-xs text-gray-500">Sistem geneli</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Crown className="h-7 w-7 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Patron</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalPatrons}</p>
                <p className="text-xs text-gray-500">{stats.inactivePatrons} pasif</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Users className="h-7 w-7 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Personel</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalStaff}</p>
                <p className="text-xs text-gray-500">Tüm lokasyonlar</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-7 w-7 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Ciro</p>
                <p className="text-3xl font-bold text-gray-900">
                  ₺{stats.totalRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-500">Sistem geneli</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Patrons */}
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Son Kayıt Olan Patronlar</h3>
            <Link to="/admin/users" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Tümünü Gör
            </Link>
          </div>
        </div>
        <div className="card-body">
          {recentPatrons.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patron
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İletişim
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lokasyonlar
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kayıt Tarihi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentPatrons.map((patron) => (
                    <tr key={patron.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Crown className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{patron.full_name}</div>
                            <div className="text-sm text-gray-500">@{patron.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{patron.email}</div>
                        <div className="text-sm text-gray-500">{patron.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {patron.locations ? patron.locations.length : 0} lokasyon
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(patron.is_active)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(patron.created_at).toLocaleDateString('tr-TR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz patron kaydı bulunmuyor</p>
            </div>
          )}
        </div>
      </div>

      {/* Management Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/locations" className="card hover:shadow-md transition-shadow group">
          <div className="card-body text-center">
            <Building2 className="h-16 w-16 text-blue-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Lokasyon Yönetimi</h3>
            <p className="text-gray-600">Tüm lokasyonları görüntüle, yönet ve patronlara ata</p>
            <div className="mt-4 text-sm text-blue-600 font-medium">
              {stats.totalLocations} lokasyon →
            </div>
          </div>
        </Link>

        <Link to="/admin/users" className="card hover:shadow-md transition-shadow group">
          <div className="card-body text-center">
            <Users className="h-16 w-16 text-green-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Kullanıcı Yönetimi</h3>
            <p className="text-gray-600">Patronları ve personeli yönet, yetkilendirme yap</p>
            <div className="mt-4 text-sm text-green-600 font-medium">
              {stats.totalPatrons + stats.totalStaff} kullanıcı →
            </div>
          </div>
        </Link>

        <Link to="/admin/reports" className="card hover:shadow-md transition-shadow group">
          <div className="card-body text-center">
            <TrendingUp className="h-16 w-16 text-purple-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sistem Raporları</h3>
            <p className="text-gray-600">Sistem geneli raporları ve analitikleri görüntüle</p>
            <div className="mt-4 text-sm text-purple-600 font-medium">
              Detaylı raporlar →
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default SystemAdminDashboard;